import { Link } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import SectionHeading from "@/components/base/SectionHeading";
import MovieCard from "@/components/base/MovieCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryQuickLinks from "@/pages/home/components/CategoryQuickLinks";
import { movies } from "@/mocks/movies";
import { news } from "@/mocks/news";
import { specials } from "@/mocks/specials";
import { usePageMeta } from "@/hooks/usePageMeta";

const stats = [
  { value: "120+", label: "收录经典影片" },
  { value: "40+", label: "影评与解读" },
  { value: "10", label: "年份跨度" },
];

export default function Home() {
  usePageMeta(
    "80s电影-经典80年代电影资源与影视资讯",
    "80s电影网站专注于提供丰富的80年代经典电影资源和影视资讯，涵盖剧情介绍、演员信息和电影评论，助您回味那个精彩年代的银幕作品。",
    "80s电影,80年代电影,经典电影,怀旧电影,老电影,电影资源,影视资讯,电影推荐"
  );

  const latestMovies = movies.slice(0, 8);
  const latestNews = news.slice(0, 5);

  return (
    <PageLayout>
      <section className="w-full border-b border-background-200 bg-background-50">
        <div className="mx-auto flex w-full max-w-6xl flex-col items-center px-4 py-8 text-center md:py-10">
          <div className="flex items-center gap-3">
            <span className="hidden h-px w-12 bg-background-300 sm:block"></span>
            <span className="font-label text-xs uppercase tracking-[0.3em] text-accent-600">
              Since the Eighties
            </span>
            <span className="hidden h-px w-12 bg-background-300 sm:block"></span>
          </div>
          <h1 className="mt-3 font-heading text-3xl font-black tracking-tight text-foreground-950 md:text-5xl">
            80s电影
          </h1>
          <p className="mt-3 max-w-2xl font-label text-sm text-foreground-600 md:text-base">
            经典80年代电影资源与影视资讯 · 剧情介绍 · 演员信息 · 电影评论
          </p>
        </div>
      </section>

      <HeroCarousel />
      <CategoryQuickLinks />

      <section className="mx-auto w-full max-w-6xl px-4 pb-6 md:px-6">
        <SectionHeading
          title="最新收录电影"
          subtitle="持续更新的80年代经典片单"
          icon="ri-movie-2-line"
          moreTo="/category"
        />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {latestMovies.map((movie, index) => (
            <MovieCard key={movie.id} movie={movie} index={index} />
          ))}
        </div>
      </section>

      <section className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <SectionHeading
              title="影视资讯快览"
              subtitle="影史回顾 · 幕后故事 · 榜单盘点"
              icon="ri-newspaper-line"
              moreTo="/news"
            />
            <div className="divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
              {latestNews.map((item) => (
                <Link
                  key={item.id}
                  to={`/news/${item.id}`}
                  className="group flex gap-4 p-4 transition-colors hover:bg-background-100"
                >
                  <div className="h-20 w-28 shrink-0 overflow-hidden rounded-md bg-primary-950">
                    <img
                      src={item.cover}
                      alt={item.title}
                      title={`${item.title} 80s电影资讯`}
                      className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 font-label text-xs text-foreground-500">
                      <span className="rounded-sm bg-secondary-100 px-1.5 py-0.5 text-secondary-900">
                        {item.category}
                      </span>
                      <span>{item.date}</span>
                    </div>
                    <h3 className="mt-1.5 font-heading text-base font-semibold text-foreground-950 line-clamp-1 group-hover:text-accent-600">
                      {item.title}
                    </h3>
                    <p className="mt-1 font-label text-sm text-foreground-600 line-clamp-2">
                      {item.summary}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div>
            <SectionHeading
              title="经典专题"
              subtitle="按主题重温"
              icon="ri-stack-line"
              moreTo="/specials"
              moreLabel="全部专题"
            />
            <div className="space-y-4">
              {specials.map((special) => (
                <Link
                  key={special.id}
                  to="/specials"
                  className="group block overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-accent-300"
                >
                  <div className="relative h-36 w-full overflow-hidden bg-primary-950">
                    <img
                      src={special.cover}
                      alt={special.title}
                      title={`${special.title} 80年代电影专题`}
                      className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 to-transparent"></div>
                    <div className="absolute bottom-3 left-3 right-3">
                      <h3 className="font-heading text-lg font-bold text-background-50">
                        {special.title}
                      </h3>
                      <p className="font-label text-xs text-background-200">
                        {special.subtitle}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-primary-50">
        <div className="mx-auto flex max-w-6xl flex-col items-start gap-8 px-4 py-12 md:flex-row md:items-center md:px-6">
          <div className="flex-1">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              关于 80s电影
            </h2>
            <p className="mt-4 max-w-2xl font-label text-sm leading-relaxed text-foreground-700 md:text-base">
              我们相信，80年代是银幕想象力最奔放的十年。这里收录了那个年代的经典影片、
              剧情介绍、演员信息与影评解读，也记录着关于电影修复、幕后制作与影史变迁的资讯。
              无论你是重温旧梦的影迷，还是初次相识的年轻观众，都希望你能在这里找到属于自己的那部电影。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/category"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                进入电影分类
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/news"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-300 px-5 py-2.5 font-label text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100"
              >
                浏览影视资讯
              </Link>
            </div>
          </div>
          <div className="grid w-full grid-cols-3 gap-4 md:w-auto">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-lg border border-background-200 bg-background-50 px-4 py-5 text-center"
              >
                <p className="font-heading text-2xl font-black text-accent-600">
                  {stat.value}
                </p>
                <p className="mt-1 font-label text-xs text-foreground-600">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PageLayout>
  );
}